import classNames from 'classnames'
import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../common/utils/format-utils.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Plus } from '../../../../../components/icon/solar-icons.js'
import { formatNumber } from '../../../utils/format.js'
import type {
    CompensationLineItem,
    CompensationLineItemGroup,
    CompensationLineItemInput,
    CompensationType,
    RequestConfirmFn,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'

interface CompensationLineItemsSectionProps {
    groups: CompensationLineItemGroup[]
    lang: Language
    // Admin (salary-edit) affordances. When false the section stays read-only.
    canManage: boolean
    types: CompensationType[]
    onCreate: (data: CompensationLineItemInput) => Promise<boolean>
    onUpdate: (itemId: number, data: CompensationLineItemInput) => Promise<boolean>
    onDelete: (itemId: number) => Promise<boolean>
    requestConfirm: RequestConfirmFn
}

const PERIOD_VALUES = ['monthly', 'quarterly', 'annual', 'one-off']

const periodLabel = (value: string, lang: Language): string => {
    const c = t.portal.compensation
    switch (value) {
        case 'monthly':
            return c.monthly(lang)
        case 'quarterly':
            return c.periodQuarterly(lang)
        case 'annual':
            return c.periodAnnual(lang)
        case 'one-off':
            return c.periodOneOff(lang)
        default:
            return value
    }
}

const today = (): string => new Date().toISOString().slice(0, 10)

interface LineItemFormProps {
    lang: Language
    types: CompensationType[]
    initial?: { typeId: number; item: CompensationLineItem }
    onSubmit: (data: CompensationLineItemInput) => Promise<boolean>
    onCancel: () => void
}

/** Shared add/edit form for a single line item. */
const LineItemForm = ({ lang, types, initial, onSubmit, onCancel }: LineItemFormProps) => {
    const c = t.portal.compensation
    const [typeId, setTypeId] = useState(
        initial ? String(initial.typeId) : types[0] ? String(types[0].id) : '',
    )
    const [amount, setAmount] = useState(initial ? String(initial.item.amount) : '')
    const [currency, setCurrency] = useState(initial?.item.currency ?? 'EUR')
    const [period, setPeriod] = useState(initial?.item.period ?? 'monthly')
    const [effectiveFrom, setEffectiveFrom] = useState(initial?.item.effectiveFrom ?? today())
    const [effectiveTo, setEffectiveTo] = useState(initial?.item.effectiveTo ?? '')
    const [note, setNote] = useState(initial?.item.note ?? '')
    const [saving, setSaving] = useState(false)

    // Keep a Humaans-imported period (e.g. "fixed") selectable even if it's not
    // one of the canonical options.
    const periodOptions = PERIOD_VALUES.includes(period)
        ? PERIOD_VALUES
        : [period, ...PERIOD_VALUES]

    const submit = async () => {
        if (!typeId || !amount || !period || !effectiveFrom) return
        setSaving(true)
        const ok = await onSubmit({
            compensation_type_id: Number(typeId),
            amount: Number(amount),
            currency,
            period,
            effective_from: effectiveFrom,
            effective_to: effectiveTo || null,
            note: note || null,
        })
        setSaving(false)
        if (ok) onCancel()
    }

    return (
        <div className="portal-profile__inline-form">
            <div className="portal-profile__inline-form-grid">
                <div className="portal-profile__inline-form-field">
                    <Select
                        label={c.lineItemType(lang)}
                        value={typeId}
                        onChange={(value) => setTypeId(value)}
                        options={types.map((ty) => ({ value: String(ty.id), label: ty.name }))}
                    />
                </div>
                <div className="portal-profile__inline-form-field">
                    <TextField
                        label={c.amount(lang)}
                        inputProps={{ type: 'number', min: '0', step: '0.01' }}
                        value={amount}
                        onChange={(value) => setAmount(value)}
                        placeholder="500"
                    />
                </div>
                <div className="portal-profile__inline-form-field">
                    <Select
                        label={c.currency(lang)}
                        value={currency}
                        onChange={(value) => setCurrency(value)}
                        options={[
                            { value: 'EUR', label: 'EUR' },
                            { value: 'USD', label: 'USD' },
                            { value: 'GBP', label: 'GBP' },
                        ]}
                    />
                </div>
                <div className="portal-profile__inline-form-field">
                    <Select
                        label={c.period(lang)}
                        value={period}
                        onChange={(value) => setPeriod(value)}
                        options={periodOptions.map((p) => ({
                            value: p,
                            label: periodLabel(p, lang),
                        }))}
                    />
                </div>
                <div className="portal-profile__inline-form-field">
                    <TextField
                        label={c.effectiveFrom(lang)}
                        inputProps={{ type: 'date' }}
                        value={effectiveFrom}
                        onChange={(value) => setEffectiveFrom(value)}
                    />
                    {initial && (
                        <p className="portal-profile__inline-form-hint">
                            {c.lineItemDateChangeHint(lang)}
                        </p>
                    )}
                </div>
                <div className="portal-profile__inline-form-field">
                    <TextField
                        label={c.endDate(lang)}
                        inputProps={{ type: 'date' }}
                        value={effectiveTo}
                        onChange={(value) => setEffectiveTo(value)}
                    />
                </div>
                <div className="portal-profile__inline-form-field portal-profile__inline-form-field--full">
                    <TextField
                        label={c.note(lang)}
                        value={note}
                        onChange={(value) => setNote(value)}
                        placeholder={c.notePlaceholder(lang)}
                    />
                </div>
            </div>
            <div className="portal-profile__inline-form-actions">
                <Button
                    text={saving ? c.saving(lang) : c.saveChange(lang)}
                    size="small"
                    isDisabled={saving || !typeId || !amount || !effectiveFrom}
                    onClick={() => void submit()}
                />
                <Button
                    text={c.cancelAction(lang)}
                    variant="secondary"
                    size="small"
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

/** "Additional compensation" section — read-only, plus admin add/edit/delete. */
export const CompensationLineItemsSection = ({
    groups,
    lang,
    canManage,
    types,
    onCreate,
    onUpdate,
    onDelete,
    requestConfirm,
}: CompensationLineItemsSectionProps) => {
    const ph = t.portal
    const c = t.portal.compensation
    const [adding, setAdding] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)

    const confirmDelete = (itemId: number) =>
        requestConfirm(c.deleteLineItemConfirm(lang), () => void onDelete(itemId), {
            confirmLabel: c.deleteAction(lang),
            variant: 'danger',
        })

    return (
        <div className="portal-profile__comp-line-items">
            <h3 className="portal-profile__section-title" style={{ marginTop: '2rem' }}>
                {ph.compensation.lineItemsTitle(lang)}
            </h3>

            {groups.length === 0 && !adding && (
                <div className="portal-profile__empty-state">
                    {ph.compensation.lineItemsEmpty(lang)}
                </div>
            )}

            {groups.map((group) => (
                <div key={group.typeId} className="portal-profile__comp-history">
                    <h4 className="portal-profile__subsection-title">{group.typeName}</h4>
                    <div className="portal-profile__contracts">
                        {group.items.map((item) =>
                            editingId === item.id ? (
                                <LineItemForm
                                    key={item.id}
                                    lang={lang}
                                    types={types}
                                    initial={{ typeId: group.typeId, item }}
                                    onSubmit={(data) => onUpdate(item.id, data)}
                                    onCancel={() => setEditingId(null)}
                                />
                            ) : (
                                <div
                                    key={item.id}
                                    className={classNames(
                                        'portal-profile__contract-card',
                                        !item.isCurrent &&
                                            'portal-profile__contract-card--inactive',
                                    )}
                                >
                                    <div className="portal-profile__contract-header">
                                        <span className="portal-profile__comp-card-amount">
                                            {item.currency} {formatNumber(item.amount, lang)}
                                        </span>
                                        <span className="portal-profile__comp-card-period">
                                            / {periodLabel(item.period, lang)}
                                        </span>
                                        {item.isCurrent && (
                                            <span className="portal-timeline__badge portal-timeline__badge--current">
                                                {ph.compensation.current(lang)}
                                            </span>
                                        )}
                                        {canManage && (
                                            <span className="portal-profile__comp-card-actions">
                                                <Button
                                                    variant="tertiary"
                                                    size="small"
                                                    text={c.editAction(lang)}
                                                    onClick={() => {
                                                        setAdding(false)
                                                        setEditingId(item.id)
                                                    }}
                                                />
                                                <Button
                                                    variant="tertiary"
                                                    size="small"
                                                    text={c.deleteAction(lang)}
                                                    onClick={() => confirmDelete(item.id)}
                                                />
                                            </span>
                                        )}
                                    </div>
                                    <div className="portal-profile__comp-details">
                                        <div className="portal-profile__comp-detail">
                                            <span className="portal-profile__field-label">
                                                {ph.compensation.effectiveFrom(lang)}
                                            </span>
                                            <span className="portal-profile__field-value">
                                                {formatDisplayDate(item.effectiveFrom)}
                                            </span>
                                        </div>
                                        {item.effectiveTo && (
                                            <div className="portal-profile__comp-detail">
                                                <span className="portal-profile__field-label">
                                                    {ph.compensation.endDate(lang)}
                                                </span>
                                                <span className="portal-profile__field-value">
                                                    {formatDisplayDate(item.effectiveTo)}
                                                </span>
                                            </div>
                                        )}
                                        {item.note && (
                                            <div className="portal-profile__comp-detail">
                                                <span className="portal-profile__field-label">
                                                    {ph.compensation.note(lang)}
                                                </span>
                                                <span className="portal-profile__field-value">
                                                    {item.note}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ),
                        )}
                    </div>
                </div>
            ))}

            {canManage &&
                (adding ? (
                    <LineItemForm
                        lang={lang}
                        types={types}
                        onSubmit={onCreate}
                        onCancel={() => setAdding(false)}
                    />
                ) : (
                    <Button
                        variant="tertiary"
                        size="small"
                        iconLeft={Plus}
                        text={c.addLineItem(lang)}
                        onClick={() => {
                            setEditingId(null)
                            setAdding(true)
                        }}
                    />
                ))}
        </div>
    )
}
